import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Phone, MessageCircle, HelpCircle, MapPin, Languages, Award, Scale, Star, CalendarDays, IdCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import Seo from "@/components/Seo";
import LegalDisclaimerBanner from "@/components/LegalDisclaimerBanner";
import { attorneyJsonLd, breadcrumbJsonLd, lawyerLegalServiceJsonLd } from "@/lib/seo";
import { buildLawyerBio, lawyerSummaryLine } from "@/lib/lawyerContent";
import { OnlineBadge } from "@/components/OnlineBadge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import imageUrl from "@/lib/imageUrl";

const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;

// Fire-and-forget contact event tracker (never blocks the CTA)
function trackContact(lawyerId, lawyerName, eventType) {
  try {
    api.post("/public/contact-events", {
      lawyer_id: lawyerId,
      lawyer_name: lawyerName,
      event_type: eventType,
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
    }).catch(() => {}); // swallow errors silently
  } catch (_) {}
}

function QueryDialog({ lawyer, lang }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name || !form.phone) { toast.error(lang === "hi" ? "\u0928\u093e\u092e \u0914\u0930 \u092b\u093c\u094b\u0928 \u0906\u0935\u0936\u094d\u092f\u0915 \u0939\u0948\u0902" : "Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post("/public/callback-requests", {
        name: form.name, phone: form.phone, email: "",
        message: `[Query for ${lawyer.name}] ${form.message}`,
        request_type: "query", preferred_language: lang,
      });
      toast.success(lang === "hi" ? "\u0906\u092a\u0915\u093e \u0938\u0935\u093e\u0932 \u092d\u0947\u091c \u0926\u093f\u092f\u093e \u0917\u092f\u093e" : "Your query has been sent");
      setOpen(false); setForm({ name: "", phone: "", message: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send query"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2" data-testid="lawyer-query-btn">
          <HelpCircle className="h-4 w-4" /> {lang === "hi" ? "\u0938\u0935\u093e\u0932 \u092a\u0942\u091b\u0947\u0902" : "Ask a query"}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="lawyer-query-dialog">
        <DialogHeader><DialogTitle>{lang === "hi" ? `\u0938\u0935\u093e\u0932 \u2014 ${lawyer.name}` : `Query \u2014 ${lawyer.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{lang === "hi" ? "\u0928\u093e\u092e" : "Your name"}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="query-name" /></div>
          <div><Label>{lang === "hi" ? "\u092b\u093c\u094b\u0928" : "Phone"}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="query-phone" /></div>
          <div><Label>{lang === "hi" ? "\u0906\u092a\u0915\u093e \u0938\u0935\u093e\u0932" : "Your question"}</Label><Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="query-message" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="query-submit">{busy ? "\u2026" : (lang === "hi" ? "\u092d\u0947\u091c\u0947\u0902" : "Send query")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingBox({ lawyer, lang }) {
  const { data: slots } = useQuery({
    queryKey: ["lawyer-slots", lawyer.id],
    queryFn: async () => (await api.get(`/public/lawyers/${lawyer.id}/slots`)).data,
  });
  const [slotId, setSlotId] = useState("");
  const [form, setForm] = useState({ litigant_name: "", litigant_phone: "", litigant_email: "", issue_summary: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const book = async () => {
    if (!slotId) { toast.error(lang === "hi" ? "\u0915\u0943\u092a\u092f\u093e \u090f\u0915 \u0938\u094d\u0932\u0949\u091f \u091a\u0941\u0928\u0947\u0902" : "Please pick a slot"); return; }
    if (!form.litigant_name || !form.litigant_phone) { toast.error(lang === "hi" ? "\u0928\u093e\u092e \u0914\u0930 \u092b\u093c\u094b\u0928 \u0906\u0935\u0936\u094d\u092f\u0915 \u0939\u0948\u0902" : "Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post("/public/bookings", { lawyer_id: lawyer.id, slot_id: slotId, ...form, preferred_language: lang });
      setDone(true);
      toast.success(lang === "hi" ? "\u092c\u0941\u0915\u093f\u0902\u0917 \u092a\u094d\u0930\u093e\u092a\u094d\u0924 \u0939\u0941\u0908" : "Booking received");
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };

  if (done) return <div className="rounded-xl border bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="booking-success">{lang === "hi" ? "\u0927\u0928\u094d\u092f\u0935\u093e\u0926! \u0939\u092e\u093e\u0930\u0940 \u091f\u0940\u092e \u0936\u0940\u0918\u094d\u0930 \u0938\u0902\u092a\u0930\u094d\u0915 \u0915\u0930\u0947\u0917\u0940\u0964" : "Thank you! Our team will confirm your free slot shortly."}</div>;

  return (
    <div className="space-y-3" data-testid="lawyer-booking">
      <p className="text-sm font-semibold">{lang === "hi" ? "\u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915 \u0938\u094d\u0932\u0949\u091f \u091a\u0941\u0928\u0947\u0902" : "Pick a free slot"}</p>
      <div className="flex flex-wrap gap-2">
        {(slots || []).length === 0 && <p className="text-sm text-muted-foreground">{lang === "hi" ? "\u0905\u092d\u0940 \u0915\u094b\u0908 \u0938\u094d\u0932\u0949\u091f \u0909\u092a\u0932\u092c\u094d\u0927 \u0928\u0939\u0940\u0902" : "No open slots right now \u2014 send a query for a callback."}</p>}
        {(slots || []).map((s) => (
          <button key={s.id} type="button" onClick={() => setSlotId(s.id)} data-testid="slot-option"
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${slotId === s.id ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
            {s.date} \u00b7 {s.start_time}
          </button>
        ))}
      </div>
      {(slots || []).length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input placeholder={lang === "hi" ? "\u0906\u092a\u0915\u093e \u0928\u093e\u092e" : "Your name"} value={form.litigant_name} onChange={(e) => setForm({ ...form, litigant_name: e.target.value })} data-testid="book-name" />
          <Input placeholder={lang === "hi" ? "\u092b\u093c\u094b\u0928" : "Phone"} value={form.litigant_phone} onChange={(e) => setForm({ ...form, litigant_phone: e.target.value })} data-testid="book-phone" />
          <Input placeholder={lang === "hi" ? "\u0908\u092e\u0947\u0932 (\u0935\u0948\u0915\u0932\u094d\u092a\u093f\u0915)" : "Email (optional)"} value={form.litigant_email} onChange={(e) => setForm({ ...form, litigant_email: e.target.value })} data-testid="book-email" />
          <Input placeholder={lang === "hi" ? "\u092e\u093e\u092e\u0932\u093e (\u0938\u0902\u0915\u094d\u0937\u0947\u092a)" : "Issue (short)"} value={form.issue_summary} onChange={(e) => setForm({ ...form, issue_summary: e.target.value })} data-testid="book-issue" />
          <p className="sm:col-span-2 text-[11px] leading-snug text-muted-foreground/80" data-testid="intake-disclaimer">Disclaimer: All initial guidance provided is for general educational purposes only. Submission does not establish an attorney-client relationship.</p>
          <Button className="sm:col-span-2" onClick={book} disabled={busy} data-testid="book-submit">{busy ? "\u2026" : (lang === "hi" ? "\u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915 \u0938\u094d\u0932\u0949\u091f \u092c\u0941\u0915 \u0915\u0930\u0947\u0902" : "Book free slot")}</Button>
        </div>
      )}
    </div>
  );
}

export default function LawyerProfile() {
  const { id } = useParams();
  const { L, lang } = useLanguage();
  const { data: lw, isLoading, isError } = useQuery({
    queryKey: ["lawyer", id],
    queryFn: async () => (await api.get(`/public/lawyers/${id}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6"><Skeleton className="h-64 w-full rounded-2xl" /></div>;
  if (isError || !lw) return (
    <div className="mx-auto max-w-5xl px-4 py-24 text-center sm:px-6">
      <p className="text-muted-foreground">{lang === "hi" ? "\u0935\u0915\u0940\u0932 \u0928\u0939\u0940\u0902 \u092e\u093f\u0932\u093e\u0964" : "Lawyer not found."}</p>
      <Button asChild variant="link"><Link to="/supreme-court-lawyers">{lang === "hi" ? "\u0938\u092d\u0940 \u0935\u0915\u0940\u0932 \u0926\u0947\u0916\u0947\u0902" : "View all lawyers"}</Link></Button>
    </div>
  );

  const helps = lang === "hi" ? lw.helps_hi || lw.helps_en : lw.helps_en;
  const title = `${lw.name} \u2014 Supreme Court Advocate${lw.specialization_en ? " (" + lw.specialization_en + ")" : ""}${lw.location ? ", " + lw.location : ""} | Litigation India`;
  const bioParas = buildLawyerBio(lw, lang);
  const metaDesc = (lawyerSummaryLine(lw) + ". Free pro bono initial guidance on Supreme Court matters.").slice(0, 158);

  return (
    <div>
      <Seo
        title={title}
        description={metaDesc}
        keywords={`${lw.name}, Supreme Court advocate, Supreme Court lawyer${lw.location ? `, Supreme Court lawyer in ${lw.location}` : ""}, senior counsel, ${lw.specialization_en || ""}, ${(lw.helps_en || []).join(", ")}, Bar Council enrolled advocate, advocate on record`}
        path={`/supreme-court-lawyers/${lw.id}`}
        image={imageUrl(lw.photo, { w: 600 }) || undefined}
        type="profile"
        jsonLd={[
          lawyerLegalServiceJsonLd(lw),
          attorneyJsonLd(lw),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Supreme Court Lawyers", path: "/supreme-court-lawyers" },
            { name: lw.name, path: `/supreme-court-lawyers/${lw.id}` },
          ]),
        ]}
      />

      <div className="li-hero-gradient border-b">
        <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
          <Link to="/supreme-court-lawyers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> {lang === "hi" ? "\u0938\u092d\u0940 \u0935\u0915\u0940\u0932" : "All lawyers"}
          </Link>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Avatar className="h-24 w-24">
              <AvatarImage src={imageUrl(lw.photo, { w: 256 })} alt={lw.name} width={96} height={96} fetchPriority="high" decoding="async" className="object-cover" />
              <AvatarFallback className="text-2xl">{lw.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-serif text-3xl font-bold tracking-tight" data-testid="lawyer-name">{lw.name}</h1>
                {lw.is_founder && <Badge className="gap-1 bg-amber-400 text-amber-950 hover:bg-amber-400"><Star className="h-3 w-3" /> {lang === "hi" ? "\u0938\u0902\u0938\u094d\u0925\u093e\u092a\u0915" : "Founder"}</Badge>}
              </div>
              <p className="mt-1 text-primary font-medium">{L(lw, "specialization")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{lang === "hi" ? "\u0905\u0927\u093f\u0935\u0915\u094d\u0924\u093e, \u092d\u093e\u0930\u0924 \u0915\u093e \u0938\u0930\u094d\u0935\u094b\u091a\u094d\u091a \u0928\u094d\u092f\u093e\u092f\u093e\u0932\u092f" : "Advocate, Supreme Court of India"}</p>
              {lw.online_available && <div className="mt-2"><OnlineBadge lang={lang} size="lg" /></div>}
            </div>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer Banner */}
      <LegalDisclaimerBanner />

      <div className="mx-auto grid w-full max-w-5xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section data-testid="lawyer-biography">
            <h2 className="font-serif text-xl font-bold">{lang === "hi" ? "परिचय" : `About ${lw.name}`}</h2>
            <div className="mt-2 space-y-3 reading-prose text-muted-foreground">
              {bioParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </section>
          <section className="grid gap-3 rounded-2xl border bg-card p-4 sm:grid-cols-2" data-testid="lawyer-facts">
            {lw.experience_years ? <p className="flex items-center gap-2 text-sm"><Award className="h-4 w-4 text-primary" /> {lw.experience_years}+ {lang === "hi" ? "\u0935\u0930\u094d\u0937 \u0905\u0928\u0941\u092d\u0935" : "years of experience"}</p> : null}
            {lw.location ? <p className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-primary" /> {lang === "hi" ? "शहर" : "City"}: {lw.location}</p> : null}
            {lw.languages?.length ? <p className="flex items-center gap-2 text-sm"><Languages className="h-4 w-4 text-primary" /> {lw.languages.join(", ")}</p> : null}
            {lw.bar_council_id ? <p className="flex items-center gap-2 text-sm"><IdCard className="h-4 w-4 text-primary" /> {lang === "hi" ? "\u092c\u093e\u0930 \u0915\u094c\u0902\u0938\u093f\u0932 \u0928\u093e\u092e\u093e\u0902\u0915\u0928" : "Bar Council enrolment"}: <span className="font-mono">{lw.bar_council_id}</span></p> : null}
            <p className="flex items-center gap-2 text-sm"><Scale className="h-4 w-4 text-primary" /> {lang === "hi" ? "न्यायालय: भारत का सर्वोच्च न्यायालय" : "Court: Supreme Court of India"}</p>
          </section>
          {helps?.length ? (
            <section data-testid="lawyer-practice-areas">
              <h2 className="font-serif text-xl font-bold">{lang === "hi" ? "\u0915\u093e\u0930\u094d\u092f\u0915\u094d\u0937\u0947\u0924\u094d\u0930" : "Practice areas & specialisations"}</h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {helps.map((h, i) => <span key={i} className="rounded-full bg-secondary px-3 py-1 text-sm text-secondary-foreground">{h}</span>)}
              </div>
            </section>
          ) : null}
          <section data-testid="lawyer-seo-copy">
            <h2 className="font-serif text-xl font-bold">{lang === "hi" ? "\u0915\u093e\u0928\u0942\u0928\u0940 \u0938\u0939\u093e\u092f\u0924\u093e" : "How a consultation works"}</h2>
            <div className="mt-2 space-y-3 reading-prose text-muted-foreground">
              <p>
                {lang === "hi"
                  ? `${lw.name} से निःशुल्क प्रारंभिक परामर्श के लिए नीचे एक खुला स्लॉट चुनें या प्रश्न भेजें। अपने मामले का संक्षिप्त विवरण, संबंधित आदेश और वर्तमान स्थिति साझा करें। यह मार्गदर्शन शैक्षिक है और औपचारिक वकील नियुक्ति का विकल्प नहीं है।`
                  : `To consult ${lw.name}, pick an open slot below or send a query with a short description of your matter, the order you are aggrieved by and the stage your case is at — first information report, trial, High Court judgment or tribunal order. ${lw.name.split(" ")[0]} will explain your position in plain language, identify whether a Special Leave Petition, appeal or writ is the appropriate route, and outline the documents you should gather.`}
              </p>
              {lang !== "hi" && (
                <p>
                  Whether you are searching for a <strong>Supreme Court lawyer{lw.location ? ` in ${lw.location}` : ""}</strong>, an advocate experienced in <strong>{lw.specialization_en || "Supreme Court litigation"}</strong>, or simply an honest second opinion on whether your case is fit for the Supreme Court of India, this initial guidance is free. Formal engagement, fees and the briefing of senior counsel are discussed only if you decide to proceed.
                </p>
              )}
            </div>
          </section>
          <section className="rounded-2xl border p-5">
            <h2 className="font-serif text-xl font-bold">{lang === "hi" ? "\u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915 \u092a\u0930\u093e\u092e\u0930\u094d\u0936 \u092c\u0941\u0915 \u0915\u0930\u0947\u0902" : "Book a complimentary consultation"}</h2>
            <div className="mt-3"><BookingBox lawyer={lw} lang={lang} /></div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="lawyer-connect-panel">
            <p className="font-serif text-lg font-bold">{lang === "hi" ? "\u0938\u0902\u092a\u0930\u094d\u0915 \u0915\u0930\u0947\u0902" : "Connect"}</p>
            <p className="mt-1 text-xs text-muted-foreground">{lang === "hi" ? "\u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915, \u092a\u094d\u0930\u094b \u092c\u094b\u0928\u094b \u092e\u093e\u0930\u094d\u0917\u0926\u0930\u094d\u0936\u0928\u0964" : "Complimentary pro bono guidance."}</p>
            <div className="mt-4 space-y-2">
              {lw.phone && (
                <Button asChild className="w-full justify-start gap-2" data-testid="lawyer-call-btn">
                  <a
                    href={`tel:${lw.phone.replace(/\s/g, "")}`}
                    onClick={() => trackContact(lw.id, lw.name, "call")}
                  >
                    <Phone className="h-4 w-4" /> {lang === "hi" ? "कॉल करें" : "Call"} · +91 {lw.phone.replace(/^\+?91/, "").replace(/\s/g, "")}
                  </a>
                </Button>
              )}
              {(lw.whatsapp || lw.phone) && (
                <Button asChild variant="outline" className="w-full justify-start gap-2" data-testid="lawyer-chat-btn">
                  <a
                    href={waLink(lw.whatsapp || lw.phone)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackContact(lw.id, lw.name, "whatsapp")}
                  >
                    <MessageCircle className="h-4 w-4" /> {lang === "hi" ? "व्हाट्सऐप चैट" : "Chat on WhatsApp"}
                  </a>
                </Button>
              )}
              <QueryDialog lawyer={lw} lang={lang} />
            </div>
            {typeof lw.available_slots === "number" && (
              <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="h-4 w-4" /> {lw.available_slots} {lang === "hi" ? "\u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915 \u0938\u094d\u0932\u0949\u091f \u0909\u092a\u0932\u092c\u094d\u0927" : "free slots available"}</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
